import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';

const quizQuestions = [
  {
    question: "Which type of burnout is caused by emotional and physical exhaustion from daily life?",
    options: [
      "Work Burnout",
      "Personal Burnout",
      "Client Burnout",
      "None of the above"
    ],
    correctAnswer: 1,
    explanation: "Personal Burnout is emotional and physical exhaustion from daily life."
  },
  {
    question: "What is the '20-20-20' Reset Rule?",
    options: [
      "Work for 20 hours, rest for 20 minutes, sleep for 20 seconds",
      "Every 20 minutes of work, take 20 seconds to stretch, breathe, or look 20 feet away",
      "Take 20 breaks every 20 minutes",
      "Work for 20 days, rest for 20 days, repeat"
    ],
    correctAnswer: 1,
    explanation: "The '20-20-20' Reset Rule is: every 20 minutes of work, take 20 seconds to stretch, breathe, or look 20 feet away."
  },
  {
    question: "Burnout is a sign of:",
    options: [
      "Weakness",
      "Your system asking for balance and recovery",
      "Laziness",
      "Success"
    ],
    correctAnswer: 1,
    explanation: "Burnout is not a weakness – it's your system asking for balance and recovery."
  },
  {
    question: "Which of the following can help prevent burnout?",
    options: [
      "Ignoring stress",
      "Taking regular breaks and practicing self-care",
      "Working nonstop",
      "Avoiding support"
    ],
    correctAnswer: 1,
    explanation: "Taking regular breaks and practicing self-care can help prevent burnout."
  },
  {
    question: "A healthy work-life balance can help you:",
    options: [
      "Feel more energized and less stressed",
      "Become more exhausted",
      "Work longer hours",
      "Ignore your health"
    ],
    correctAnswer: 0,
    explanation: "A healthy work-life balance helps you feel more energized and less stressed."
  }
];

const OccupationalModule2QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { saveProgress } = useModuleProgress('occupationalM2');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (index: number) => {
    if (selectedAnswer === null) {
      setSelectedAnswer(index);
      setShowExplanation(true);
    }
  };

  const handleNext = async () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
  await saveProgress(quizQuestions.length, true); // Mark module as completed
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">You've completed the Module 2 quiz!</p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <ul className="achievement-list">
              <li><span className="check"></span> Recognizing burnout types</li>
              <li><span className="check"></span> Applying the 20-20-20 Reset Rule</li>
              <li><span className="check"></span> Understanding burnout as a call for balance</li>
            </ul>
          </div>
          <button className="resource-button" onClick={() => navigate('/panel/learn/occupational-resources')}>Return to Resources</button>
        </div>
      </div>
    );
  }

  const currentQuiz = quizQuestions[currentQuestion];
  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left occupational-panel">
          <div className="module-header">
            <h1>Module 2 Quiz</h1>
            <p className="module-subtitle">Recognizing and Preventing Burnout</p>
            <hr className="module-divider" />
            <p className="module-tool-info">Test your understanding of burnout and recovery strategies.</p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%` }}></div>
              </div>
              <span className="progress-text">Question {currentQuestion + 1} of {quizQuestions.length}</span>
            </div>
            <div className="question-section">
              <h2 className="question-title">{currentQuiz.question}</h2>
              <div className="answer-options">
                {currentQuiz.options.map((option, idx) => (
                  <label key={idx} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={idx}
                      checked={selectedAnswer === idx}
                      onChange={() => handleAnswerSelect(idx)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button className="btn-secondary" onClick={currentQuestion === 0 ? () => navigate('/panel/learn/occupational-resources') : handleBack}>
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Next Question</button>
              ) : (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Complete Quiz</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationalModule2QuizPage;


